"use client";

import {JSX, useState} from "react";

import {NavigationTable, type NavigationTableItem} from "@/sections/content/navigation-table";
import {type SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import {useTranslation} from "@/shared/i18n";
import {ExpandableTextWithButton} from "@/shared/ui/data-display/expandable-text/expandable-text-with-button";
import {HtmlContent} from "@/shared/ui/data-display/html-content";
import type {ContactPhoneItem} from "@/shared/ui/data-display/phone-number/phone-number";
import {Typography} from "@/shared/ui/data-display/typography";
import {ContainerBase} from "@/shared/ui/layout/container-base";
import {Modal} from "@/shared/ui/overlay/modal";

import {OwnedBig} from "./parts/owned-big/owned-big";
import type {OwnerData} from "./parts/owned/owned";
import {Owned} from "./parts/owned/owned";

import "./styles/welcome.scss";

export type WelcomeProps = {
    welcome?: string;
    title: string;
    description: string;
    space?: SpaceProps;
    owner?: OwnerData;
    contactPhones?: Array<ContactPhoneItem>;
    tableOfContent?: {
        title?: string;
        items: Array<NavigationTableItem>;
    };
    tableOfContentSpacing?: {
        mobile?: number;
        desktop?: number;
    };
    anchorId?: string;
};

export function Welcome({
    title,
    welcome,
    description,
    space,
    owner,
    contactPhones,
    tableOfContent,
    tableOfContentSpacing,
    anchorId,
}: WelcomeProps): JSX.Element {
    const sectionVars = useSectionSpace(space);

    const [isOpen, setIsOpen] = useState(false);

    const handleOpenModal = (): void => {
        setIsOpen(true);
    };

    const handleCloseModal = (): void => {
        setIsOpen(false);
    };
    const {t} = useTranslation();

    return (
        <section className="welcome" style={sectionVars} id={anchorId}>
            <ContainerBase>
                <div className="welcome__wrap">
                    <div className="welcome__description">
                        <div className="welcome__content">
                            <Typography as="div" size={{desktop: 18, mobile: 14}} className="welcome__to">
                                {welcome}
                            </Typography>
                            <Typography as="h1" className="welcome__title">
                                {title}
                            </Typography>

                            <div className="welcome__description-wrap">
                                <ExpandableTextWithButton
                                    maxLines={{desktop: 4, mobile: 4, tablet: 4}}
                                    buttonClassName="welcome__readmore-button"
                                    buttonSize="sm"
                                    charCount={description?.length}
                                    ssrEstimation={{
                                        desktop: {width: 540, fontSize: 14},
                                        tablet: {width: 540, fontSize: 14},
                                        mobile: {width: 420, fontSize: 14},
                                    }}
                                >
                                    <HtmlContent html={description}></HtmlContent>
                                </ExpandableTextWithButton>
                            </div>
                        </div>
                    </div>

                    {owner && (
                        <div className="welcome__owned">
                            <Owned
                                title={t("destination.locallyOwned")}
                                owner={owner}
                                onReadMoreClick={handleOpenModal}
                                contactPhones={contactPhones}
                            />
                        </div>
                    )}
                    {tableOfContent && tableOfContent.items.length > 0 && (
                        <div className="welcome__table-of-content">
                            <NavigationTable
                                title={tableOfContent.title}
                                items={tableOfContent.items}
                                spacing={tableOfContentSpacing}
                                preview={{
                                    mobile: "auto",
                                    desktop: tableOfContent.items.length > 4 ? 4.5 : tableOfContent.items.length,
                                }}
                            />
                        </div>
                    )}
                </div>
            </ContainerBase>

            {owner && (
                <Modal
                    className="modal_clean"
                    lockScroll={true}
                    isOpen={isOpen}
                    onClose={handleCloseModal}
                    ariaLabel="Owner"
                    buttonCloseType="outside"
                >
                    <OwnedBig owner={owner} title={t("destination.locallyOwned")} contactPhones={contactPhones}>
                        <div dangerouslySetInnerHTML={{__html: owner?.description ?? ""}}></div>
                    </OwnedBig>
                </Modal>
            )}
        </section>
    );
}
